import React, { useMemo, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import classes from './Reports.module.scss';
import {
    disableSubmitButton,
    formatDate,
    underNpiLimit,
    validateFileName,
    validateNpis
} from 'features/queryReports/utils/commonFunctions';
import QueryCreatedModal from 'features/queryReports/components/QueryCreatedModal';
import { useSubmitQuery } from 'features/queryReports/hooks/useSubmitQuery';
import { campaignEffectivenessIDs } from 'features/queryReports/utils/reportIds';
import { applyCommonOutputs } from "features/queryReports/utils/queryCommon";
import {
    CheckboxInput,
    DateRange,
    FileNameInput,
    MeasureTimeFrameSelect,
    NPIsInput,
    VisualizationToggle
} from 'features/queryReports/components/FormFields';
import campaignEffectivenessOutputFields from "features/queryReports/utils/reportOutputFields/CampaignEffectivenessOutputFields";

function QueryLogicDescription({
    timeFrameDays,
    referringNpis,
    renderingNpis,
    captureStartDate,
    captureEndDate,
    captureStartDate2,
    captureEndDate2
}) {
    const referringCount = referringNpis?.split(',').length || 0;
    const renderingCount = renderingNpis?.split(',').length || 0;

    return (
        <div><p>I would like to see patients and procedures shared between the <strong>{referringCount} Target Physician{referringCount > 1 ? 's' : ''}</strong> and <strong>{renderingCount} Destination Physician{renderingCount > 1 ? 's' : ''}</strong> in a <strong>{timeFrameDays}</strong> day window over two distinct time periods <strong>{formatDate(captureStartDate)}</strong> to <strong>{formatDate(captureEndDate)}</strong> and <strong>{formatDate(captureStartDate2)}</strong> to <strong>{formatDate(captureEndDate2)}</strong>.</p></div>
    )
}

function CampaignEffectiveness({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
    const {
        categoryId,
        reportId,
        allNpisReportId
    } = campaignEffectivenessIDs;
    const {
        state: common,
        bind,
        set,
        setOutput,
        resetCommon,
        hydrateFrom,
        selectors
    } = useCommonStateFields({
        overrides: { payerMix: payerMixValue ?? "" },
    });

    const {
        submitQuery,
        waiting,
        showModal,
        modalData,
        closeModal,
    } = useSubmitQuery({ bridge, categoryId, classes, fileName: common.fileName });

    const confirmQuerySuccess = type => {
        if (type === 1) {
            closeModal();
            bridge.onMyQueryOpen();
        } else if (type === 2) {
            closeModal();
            bridge.onQuerySubmitted();
        }
    };

    const applyIncomingParams = data => {
      if (data.reportId === allNpisReportId) {
        set("includeFacilities", true);
      } else {
        set("includeFacilities", false);
      }
    };

    useEffect(() => {
        if (initialParams) {
            hydrateFrom(initialParams);
            applyIncomingParams(initialParams);
            // any page-only hydration here as needed
        }
    }, [initialParams]);

    const queryObj = useMemo(() => {
        const retObj = {
            reportId: common.includeFacilities
                ? allNpisReportId
                : reportId,
            description: common.fileName,
            parameters: {
                referring_npis: common.npis
                    .split(',')
                    .map((subStr) => subStr.replace(/\D/g, ''))
                    .filter((item) => item !== ''),
                rendering_npis: common.npis2
                    .split(',')
                    .map((subStr) => subStr.replace(/\D/g, ''))
                    .filter((item) => item !== ''),
                start_date_first: formatDate(common.startDate),
                end_date_first: formatDate(common.endDate),
                start_date_second: formatDate(common.startDate2),
                end_date_second: formatDate(common.endDate2),
                days: common.timeFrameDays,
                canBeVisualized: common.canBeVisualized,
            },
            outputFields: [
                common.outputs.firstNPI ? 'First NPI' : null,
                common.outputs.firstPhysicianName ? 'First Physician Name' : null,
                common.outputs.firstPhysicianClassification ? 'First Physician Classification' : null,
                common.outputs.firstPhysicianSpecialization ? 'First Physician Specialization' : null,
                common.outputs.newPatients ? 'New Patients' : null,
                common.outputs.secondNPI ? 'Second NPI' : null,
                common.outputs.secondPhysicianName ? 'Second Physician Name' : null,
                common.outputs.secondPhysicianClassification ? 'Second Physician Classification' : null,
                common.outputs.secondPhysicianSpecialization ? 'Second Physician Specialization' : null,
                common.outputs.timeframe ? 'Timeframe' : null,
                common.outputs.procedureCode ? 'Procedure Code' : null,
                common.outputs.procedureDescription ? 'Procedure Description' : null,
                common.outputs.sharedPatients ? 'Shared Patients' : null,
            ].filter((item) => item),
        };

        // common outputs
        applyCommonOutputs(retObj, common.outputs, {
            allowList: [
                'sumCharges',
                'proceduresCount'
            ]
        });

        if (common.includeFacilities) {
            if (common.outputs.firstOrgNPI) {
                retObj.outputFields.push('First Organization NPI');
            }
            if (common.outputs.firstOrgName) {
                retObj.outputFields.push('First Organization Name');
            }
            if (common.outputs.firstOrgClassification) {
                retObj.outputFields.push('First Organization Classification');
            }
            if (common.outputs.firstOrgSpecialization) {
                retObj.outputFields.push('First Organization Specialization');
            }
            if (common.outputs.secondOrgNPI) {
                retObj.outputFields.push('Second Organization NPI');
            }
            if (common.outputs.secondOrgName) {
                retObj.outputFields.push('Second Organization Name');
            }
            if (common.outputs.secondOrgClassification) {
                retObj.outputFields.push('Second Organization Classification');
            }
            if (common.outputs.secondOrgSpecialization) {
                retObj.outputFields.push('Second Organization Specialization');
            }
        }

        if (common.canBeVisualized) {
            retObj.outputFields = [
                'First NPI',
                'First Physician Name',
                'First Physician Classification',
                'First Physician Specialization',
                'New Patients',
                'Second NPI',
                'Second Physician Name',
                'Second Physician Classification',
                'Second Physician Specialization',
                'Timeframe',
                'Procedure Code',
                'Procedure Description',
                'Count of Procedures',
                'Shared Patients',
                'Sum of Charges'
            ];

            if (common.includeFacilities) {
                retObj.outputFields = retObj.outputFields.concat([
                    'First Organization NPI',
                    'First Organization Name',
                    'First Organization Classification',
                    'First Organization Specialization',
                    'Second Organization NPI',
                    'Second Organization Name',
                    'Second Organization Classification',
                    'Second Organization Specialization',
                ]);
            }
        }

        return retObj;
    }, [
        common.npis,
        common.npis2,
        common.startDate,
        common.endDate,
        common.startDate2,
        common.endDate2,
        common.timeFrameDays,
        common.outputs,
        common.canBeVisualized,
        common.includeFacilities
    ]);

    function clearFields() {
        if (!showOpenMyQueries) {
          bridge?.onClickCancelQueryReport();
          return;
        }
        resetCommon(); // clears shared fields
        // clear page-only here
    }

    return (
        <div className="container-fluid p-3">
            <div className={classes["report-content-wrap"]}>
                <FileNameInput
                    fileName={common.fileName}
                    setFileName={(v) => set('fileName', v)}
                    priorQueryParams={common.priorQueryParams}
                />
                <NPIsInput
                    bind={bind}
                    common={common}
                    label="Target Physician NPIs"
                    overlayText="The first physicians to see the patient who are the target of the campaign, whom we want to measure patient volume originating from"
                />
                <NPIsInput
                    bind={bind}
                    common={common}
                    label="Destination Physician NPIs"
                    overlayText="The second physician to see the patient, where we want to see patient growth to"
                    second
                />

                <MeasureTimeFrameSelect bind={bind} categoryId={categoryId} />

                <div className="form-group row">
                    <OverlayTrigger
                        key="includeFacilities"
                        placement="left"
                        overlay={<Tooltip>Include Facilities</Tooltip>}
                    >
                        <label className="col-3 col-form-label">Include Facilities</label>
                    </OverlayTrigger>
                    <div className="col-9 d-flex align-items-center">
                        <input
                            name="includeFacilities"
                            type="checkbox"
                            className="form-check"
                            checked={common.includeFacilities}
                            {...bind.checkbox('includeFacilities')}
                        />
                    </div>
                </div>

                <DateRange
                    common={common}
                    bind={bind}
                    label="Measure Timeframe 1"
                    overlayText="Timeframe to analyze BEFORE the campaign"
                />
                <DateRange
                    common={common}
                    bind={bind}
                    second
                    label="Measure Timeframe 2"
                    overlayText="Timeframe to analyze AFTER the campaign"
                />

                <hr />
                <VisualizationToggle common={common} bind={bind} />

                {common.npis && validateNpis(common.npis) &&
                    common.npis2 && validateNpis(common.npis2) &&
                    common.timeFrameDays && (
                        <div className="row mb-3">
                            <div className={classes["querylogic-wrapper"]}>
                                <FontAwesomeIcon icon={faCircleInfo} className={classes["info-icon"]} />
                                <div>
                                    <div className={classes["header"]}>Query Logic</div>
                                    <div className={classes["body"]}>
                                        <QueryLogicDescription
                                            timeFrameDays={common.timeFrameDays}
                                            referringNpis={common.npis}
                                            renderingNpis={common.npis2}
                                            captureStartDate={common.startDate}
                                            captureEndDate={common.endDate}
                                            captureStartDate2={common.startDate2}
                                            captureEndDate2={common.endDate2}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                {common.npis && common.npis2 && common.timeFrameDays && (
                    <div className="row mb-3">
                        <div className={classes["querylogic-wrapper"]}>
                            <FontAwesomeIcon icon={faCircleInfo} className={classes["info-icon"]} />
                            <div>
                                <div className={classes["header"]}>Output Fields</div>
                                <div className={classes["body"]}>
                                    {campaignEffectivenessOutputFields.map((item, index) => (
                                        <CheckboxInput
                                            key={item.name || index}
                                            item={item}
                                            common={common}
                                            selectors={selectors}
                                            setOutput={setOutput}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {common.fileName &&
                    common.npis && validateNpis(common.npis) && underNpiLimit(common.npis) &&
                    common.npis2 && validateNpis(common.npis2) && underNpiLimit(common.npis2) &&
                    common.timeFrameDays &&
                    common.startDate && common.endDate &&
                    common.startDate2 && common.endDate2 && (
                        <div className={`row ${classes["submit-btn-wrap"]}`}>
                            <button
                                type="button"
                                className={`btn btn-primary mr-2 ${classes["submit-btn"]}`}
                                disabled={!validateFileName(common.fileName) || disableSubmitButton(common)}
                                onClick={() => submitQuery(queryObj)}
                            >
                                Submit Query
                                {waiting && (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                )}
                            </button>
                            <button
                                type="button"
                                className={`btn btn-secondary ${classes["cancel-btn"]}`}
                                onClick={() => clearFields()}
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                <QueryCreatedModal
                    show={showModal}
                    header={modalData.header}
                    body={modalData.body}
                    classes={classes}
                    onClose={() => confirmQuerySuccess(2)}
                    onOpenMyQueries={() => confirmQuerySuccess(1)}
                    showOpenMyQueries={showOpenMyQueries}
                />

            </div>
        </div>
    );
}

export default CampaignEffectiveness;